/* Hallmark · build · DcMapSVG · genre: modern-minimal · component-scope
 * theme: Cobalt (project-locked) · pre-emit critique: P5 H5 E5 S5 R5 V5
 * states: default · dragging · hover · inspecting · zoomed
 * Standards: ASHRAE TC 9.9 thermal zoning, TIA-942-C.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { RackMeta, RoutePoint, DcMeta, rackLabel } from '../../hooks/useCalculatorLogic'
import { VIEW_PPM, rackScreenRect, traySegments, rotatedRectBounds, parseAisleZones } from '../../utils/floorPlan.logic'
import { calculatorMapScaleLabel } from '../../utils/calculatorMapScale.logic'
import { getRackTemperature, getThermalColor } from '../../utils/thermalOverlay.logic'

interface TooltipState { x: number; y: number; rack: RackMeta; lbl: string }

export function DcMapSVG({
  racks,
  route,
  srcId,
  tgtId,
  onRackClick,
  dc,
  totalLength,
  aisleZones,
  focusedRackId,
  onEmptyClick,
  telemetryMap,
}: {
  racks: RackMeta[]
  route: RoutePoint[]
  srcId?: number
  tgtId?: number
  onRackClick?: (r: RackMeta) => void
  dc?: DcMeta
  totalLength?: number
  aisleZones?: unknown
  focusedRackId?: number | null
  onEmptyClick?: () => void
  telemetryMap?: Record<string | number, any> | Array<any> | null
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tr, setTr] = useState({ zoom: 1, panX: 60, panY: 40 })
  const [isDragging, setIsDragging] = useState(false)
  const [flashId, setFlashId] = useState<number | null>(null)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const dragRef = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null)

  const rawAisleZones = aisleZones ?? dc?.aisle_zones_json ?? (dc?.cable_trays_json as any)?.aisle_zones
  const aisleRects = useMemo(
    () => parseAisleZones(rawAisleZones, VIEW_PPM),
    [rawAisleZones],
  )

  const trays = useMemo(() => traySegments(dc?.cable_trays_json, VIEW_PPM), [dc?.cable_trays_json])

  const rects = useMemo(
    () => racks.map(r => ({ rack: r, ...rackScreenRect(r, VIEW_PPM) })),
    [racks],
  )

  const dcBounds = useMemo(() => {
    let minX = 0, minY = 0, maxX = 100, maxY = 100
    for (const r of rects) {
      const b = rotatedRectBounds(r)
      minX = Math.min(minX, b.minX)
      minY = Math.min(minY, b.minY)
      maxX = Math.max(maxX, b.maxX)
      maxY = Math.max(maxY, b.maxY)
    }
    for (const s of trays) {
      maxX = Math.max(maxX, s.x1, s.x2)
      maxY = Math.max(maxY, s.y1, s.y2)
      minX = Math.min(minX, s.x1, s.x2)
      minY = Math.min(minY, s.y1, s.y2)
    }
    for (const p of route ?? []) {
      const px = p.x * VIEW_PPM, py = p.y * VIEW_PPM
      maxX = Math.max(maxX, px)
      maxY = Math.max(maxY, py)
      minX = Math.min(minX, px)
      minY = Math.min(minY, py)
    }
    for (const z of aisleRects) {
      if (!Number.isFinite(z.x) || !Number.isFinite(z.y) || !Number.isFinite(z.w) || !Number.isFinite(z.h)) continue
      minX = Math.min(minX, z.x)
      minY = Math.min(minY, z.y)
      maxX = Math.max(maxX, z.x + z.w)
      maxY = Math.max(maxY, z.y + z.h)
    }
    const ox = Math.min(0, minX)
    const oy = Math.min(0, minY)
    return { w: Math.max(maxX - ox + 80, 400), h: Math.max(maxY - oy + 80, 300), ox, oy }
  }, [rects, trays, route, aisleRects])

  const dcW = dcBounds.w
  const dcH = dcBounds.h

  const fitView = useCallback(() => {
    if (!containerRef.current || racks.length === 0) return
    const { width, height } = containerRef.current.getBoundingClientRect()
    if (!width || !height) return
    const PAD = 32
    const zx = (width - PAD * 2) / Math.max(dcW, 1)
    const zy = (height - PAD * 2) / Math.max(dcH, 1)
    const zoom = Math.min(zx, zy, 2.5)
    setTr({ zoom, panX: width / 2 - (dcW / 2) * zoom, panY: height / 2 - (dcH / 2) * zoom })
  }, [racks.length, dcW, dcH])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const t = setTimeout(fitView, 30)
    const ro = new ResizeObserver(() => fitView())
    ro.observe(el)
    return () => { clearTimeout(t); ro.disconnect() }
  }, [fitView])

  useEffect(() => { if (srcId != null) { setFlashId(srcId); setTimeout(() => setFlashId(null), 900) } }, [srcId])
  useEffect(() => { if (tgtId != null) { setFlashId(tgtId); setTimeout(() => setFlashId(null), 900) } }, [tgtId])

  // flyTo animation when a rack is focused (search, command palette, or direct link)
  useEffect(() => {
    if (focusedRackId == null || !containerRef.current) return
    const target = rects.find(r => r.rack.id === focusedRackId)
    if (!target) return

    setFlashId(focusedRackId)
    const timer = setTimeout(() => setFlashId(null), 1200)

    const rect = containerRef.current.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const targetX = (target.x + target.w / 2) - dcBounds.ox
    const targetY = (target.y + target.h / 2) - dcBounds.oy
    const targetZoom = Math.max(1.5, Math.min(Math.max(tr.zoom, 1.8), 3.0))
    const offsetX = rect.width > 768 ? (rect.width - 380) / 2 : rect.width / 2
    const offsetY = rect.height / 2

    setTr({
      zoom: targetZoom,
      panX: offsetX - targetX * targetZoom,
      panY: offsetY - targetY * targetZoom,
    })

    return () => clearTimeout(timer)
  }, [focusedRackId, rects, dcBounds.ox, dcBounds.oy])

  const onMD = useCallback((e: React.MouseEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY, px: tr.panX, py: tr.panY, moved: false }
    setIsDragging(true)
  }, [tr.panX, tr.panY])
  const onMM = useCallback((e: React.MouseEvent) => {
    if (!dragRef.current) return
    const dx = e.clientX - dragRef.current.x, dy = e.clientY - dragRef.current.y
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true
    setTr(t => ({ ...t, panX: dragRef.current!.px + dx, panY: dragRef.current!.py + dy }))
  }, [])
  const onMU = useCallback(() => {
    dragRef.current = null
    setIsDragging(false)
  }, [])

  const touchRef = useRef<{ x: number; y: number; px: number; py: number; dist?: number } | null>(null)

  const onTS = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const t = e.touches[0]
      touchRef.current = { x: t.clientX, y: t.clientY, px: tr.panX, py: tr.panY }
    } else if (e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1]
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
      const cx = (t1.clientX + t2.clientX) / 2
      const cy = (t1.clientY + t2.clientY) / 2
      touchRef.current = { x: cx, y: cy, px: tr.panX, py: tr.panY, dist }
    }
  }, [tr.panX, tr.panY])

  const onTM = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current) return
    if (e.touches.length === 1 && touchRef.current.dist == null) {
      const t = e.touches[0]
      const dx = t.clientX - touchRef.current.x
      const dy = t.clientY - touchRef.current.y
      setTr(prev => ({ ...prev, panX: touchRef.current!.px + dx, panY: touchRef.current!.py + dy }))
    } else if (e.touches.length === 2 && touchRef.current.dist != null) {
      const t1 = e.touches[0], t2 = e.touches[1]
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
      if (dist > 0 && touchRef.current.dist > 0) {
        const scale = dist / touchRef.current.dist
        const cx = (t1.clientX + t2.clientX) / 2
        const cy = (t1.clientY + t2.clientY) / 2

        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect()
          const fx = cx - rect.left, fy = cy - rect.top
          setTr(prev => {
            const nz = Math.max(0.08, Math.min(prev.zoom * scale, 10))
            const lx = (fx - prev.panX) / prev.zoom, ly = (fy - prev.panY) / prev.zoom
            return { zoom: nz, panX: fx - lx * nz, panY: fy - ly * nz }
          })
          touchRef.current.dist = dist
        }
      }
    }
  }, [])

  const onTE = useCallback(() => {
    touchRef.current = null
  }, [])

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const rect = containerRef.current!.getBoundingClientRect()
    const fx = e.clientX - rect.left, fy = e.clientY - rect.top
    setTr(t => {
      const f = e.deltaY < 0 ? 1.15 : 1 / 1.15
      const nz = Math.max(0.08, Math.min(t.zoom * f, 10))
      const lx = (fx - t.panX) / t.zoom, ly = (fy - t.panY) / t.zoom
      return { zoom: nz, panX: fx - lx * nz, panY: fy - ly * nz }
    })
  }, [])

  const cablePath = useMemo(() => {
    if (!route || route.length < 2) return null
    const pts = route.map(p => `${p.x * VIEW_PPM},${p.y * VIEW_PPM}`)
    return `M ${pts[0]} L ${pts.slice(1).join(' L ')}`
  }, [route])

  const badgePt = useMemo(() => {
    const srcRack = rects.find(r => r.rack.id === srcId)
    const tgtRack = rects.find(r => r.rack.id === tgtId)
    if (!srcRack || !tgtRack || !totalLength) return null
    return {
      x: (srcRack.x + srcRack.w / 2 + tgtRack.x + tgtRack.w / 2) / 2,
      y: Math.min(srcRack.y, tgtRack.y) - 16,
    }
  }, [totalLength, rects, srcId, tgtId])

  if (racks.length === 0) {
    return (
      <div ref={containerRef} className="w-full h-full flex flex-col items-center justify-center gap-2 text-xs text-text-muted">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
          <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
        </svg>
        <span>Нет стоек для отображения</span>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden select-none"
    >
      <svg width="100%" height="100%"
        onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU} onMouseLeave={onMU}
        onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE} onTouchCancel={onTE}
        onWheel={onWheel}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      >
        <defs>
          <pattern id="dc-g-min" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5"/>
          </pattern>
          <pattern id="dc-g-maj" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
          </pattern>
          <pattern id="dc-containment-cold" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(14, 165, 233, 0.16)" strokeWidth="1.5"/>
          </pattern>
          <pattern id="dc-containment-hot" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(239, 68, 68, 0.16)" strokeWidth="1.5"/>
          </pattern>
          <filter id="dc-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <style>{`
            @keyframes dc-flow { from { stroke-dashoffset: 30; } to { stroke-dashoffset: 0; } }
            @keyframes dc-flash {
              0%,100% { opacity:1; }
              25% { opacity:0.15; fill:#F59E0B; }
              60% { fill:#F59E0B; opacity:0.9; }
            }
            @keyframes dc-route-in {
              from { stroke-dashoffset: 3000; opacity:0; }
              to   { stroke-dashoffset: 0;    opacity:1; }
            }
            .dc-cable-bg  { stroke:rgba(59,130,246,0.15); stroke-width:10; fill:none; stroke-linecap:round; }
            .dc-cable     { stroke:#60A5FA; stroke-width:2.5; stroke-dasharray:14 6; stroke-linecap:round;
                            animation: dc-flow 0.6s linear infinite; fill:none; filter:url(#dc-glow); }
            .dc-cable-in  { stroke-dasharray:3000; animation: dc-route-in 0.5s ease-out forwards; }
            .dc-rack      { transition: fill 0.1s, stroke 0.1s; }
            .dc-rack-flash { animation: dc-flash 0.9s ease-in-out; }
          `}</style>
        </defs>

        <rect width="100%" height="100%" fill="var(--color-bg-app,#0B1120)"
          onClick={() => { if (!dragRef.current?.moved) onEmptyClick?.() }}/>
        <rect width="100%" height="100%" fill="url(#dc-g-maj)" style={{ pointerEvents: 'none' }}/>
        <rect width="100%" height="100%" fill="url(#dc-g-min)" style={{ pointerEvents: 'none' }}/>

        <g
          transform={`translate(${tr.panX},${tr.panY}) scale(${tr.zoom}) translate(${-dcBounds.ox},${-dcBounds.oy})`}
          style={{
            transition: isDragging || touchRef.current ? 'none' : 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
          }}
        >

          {/* Aisle Thermal & Containment Zones (rendered below trays and racks) */}
          <g className="dc-aisle-zones">
            {aisleRects.map(zone => {
              const isCold = zone.type === 'cold'
              const fill = isCold ? 'rgba(14, 165, 233, 0.12)' : 'rgba(239, 68, 68, 0.12)'
              const stroke = isCold ? 'rgba(14, 165, 233, 0.35)' : 'rgba(239, 68, 68, 0.35)'
              const textColor = isCold ? '#38BDF8' : '#F87171'
              const strokeDasharray = zone.isContained ? '6,4' : undefined
              const strokeWidth = zone.isContained ? 1.5 : 1

              const isHoriz = zone.w >= zone.h
              const cx = zone.x + zone.w / 2
              const cy = zone.y + zone.h / 2
              const minDim = Math.min(zone.w, zone.h)
              const fontSize = Math.max(8, Math.min(11, minDim * 0.24))
              const showText = minDim * tr.zoom >= 10 && minDim >= 14

              return (
                <g key={zone.id} className="dc-aisle-zone" style={{ pointerEvents: 'none' }}>
                  <rect
                    x={zone.x}
                    y={zone.y}
                    width={zone.w}
                    height={zone.h}
                    rx={2}
                    fill={fill}
                  />
                  {zone.isContained && (
                    <rect
                      x={zone.x}
                      y={zone.y}
                      width={zone.w}
                      height={zone.h}
                      rx={2}
                      fill={isCold ? 'url(#dc-containment-cold)' : 'url(#dc-containment-hot)'}
                    />
                  )}
                  <rect
                    x={zone.x}
                    y={zone.y}
                    width={zone.w}
                    height={zone.h}
                    rx={2}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                  />
                  {showText && (
                    <g transform={isHoriz ? undefined : `rotate(-90,${cx},${cy})`}>
                      <text
                        x={cx}
                        y={cy + 1}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fontSize}
                        fontFamily="'JetBrains Mono', monospace"
                        fontWeight="600"
                        fill={textColor}
                        opacity={0.85}
                        letterSpacing="0.05em"
                      >
                        {zone.label}
                      </text>
                    </g>
                  )}
                </g>
              )
            })}
          </g>

          {trays.map((s, i) => (
            <line key={`tray${i}`}
              x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
              stroke="rgba(59,130,246,0.45)" strokeWidth={4} strokeLinecap="round"
              style={{ pointerEvents: 'none' }}/>
          ))}

          {cablePath && (
            <g>
              <path d={cablePath} className="dc-cable-bg"/>
              <path d={cablePath} className="dc-cable dc-cable-in" key={cablePath}/>
            </g>
          )}

          {badgePt && totalLength && (
            <g transform={`translate(${badgePt.x},${badgePt.y})`}>
              <rect x={-40} y={-15} width={80} height={30} rx={4}
                fill="#0F172A" stroke="#3B82F6" strokeWidth={1.5} opacity={0.97}/>
              <text textAnchor="middle" dominantBaseline="central"
                fontSize="13" fontFamily="'JetBrains Mono',monospace"
                fill="#93C5FD" fontWeight="bold">
                {totalLength.toFixed(1)} м
              </text>
            </g>
          )}

          {rects.map(({ rack, x, y, w, h, rotation }) => {
            const isSrc    = rack.id === srcId
            const isTgt    = rack.id === tgtId
            const isFlash  = rack.id === flashId
            const isFocused = rack.id === focusedRackId
            const isBlocked = rack.is_blocked
            const lbl = rackLabel(rack, dc)
            const cx = x + w / 2
            const cy = y + h / 2

            const temp = getRackTemperature(rack, telemetryMap)
            const thermalStyle = getThermalColor(temp)

            let fill   = thermalStyle ? thermalStyle.fill : '#1A2744'
            let stroke = thermalStyle ? thermalStyle.stroke : '#2D3E5A'
            let sw     = thermalStyle ? 1.5 : 0.8
            if (isSrc)      { fill='rgba(245,158,11,0.25)';  stroke='#F59E0B'; sw=2.5 }
            else if (isTgt) { fill='rgba(16,185,129,0.25)';  stroke='#10B981'; sw=2.5 }
            else if (isFocused) { fill='rgba(56,189,248,0.3)'; stroke='#38BDF8'; sw=2.5 }
            else if (isBlocked) { fill='rgba(239,68,68,0.08)'; stroke='#EF4444'; sw=0.7 }

            return (
              <g key={rack.id}
                data-testid={`rack-svg-${rack.id}`}
                onClick={() => !isBlocked && !dragRef.current?.moved && onRackClick?.(rack)}
                onMouseEnter={e => {
                  const b = containerRef.current!.getBoundingClientRect()
                  setTooltip({ x: e.clientX - b.left + 16, y: e.clientY - b.top + 16, rack, lbl })
                }}
                onMouseMove={e => {
                  const b = containerRef.current!.getBoundingClientRect()
                  setTooltip(t => t ? { ...t, x: e.clientX - b.left + 16, y: e.clientY - b.top + 16 } : null)
                }}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: isBlocked ? 'not-allowed' : 'pointer' }}
              >
                <g transform={`rotate(${rotation},${cx},${cy})`}>
                <rect x={x} y={y} width={w} height={h} rx={2}
                  fill={fill} stroke={stroke} strokeWidth={sw}
                  className={`dc-rack${isFlash ? ' dc-rack-flash' : ''}`}/>
                <rect x={x+sw} y={y+sw} width={w-sw*2} height={Math.min(5, h * 0.08)} rx={1}
                  fill={isSrc ? '#F59E0B' : isTgt ? '#10B981' : 'rgba(255,255,255,0.08)'}
                  style={{ pointerEvents: 'none' }}/>
                <g transform={`rotate(-90,${cx},${cy})`}
                  style={{ pointerEvents: 'none' }}>
                  <text x={cx} y={cy+1}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize={Math.max(8, Math.min(12, w * 0.35))} fontWeight="600"
                    fontFamily="'JetBrains Mono',monospace"
                    fill={isSrc || isTgt ? '#fff' : 'rgba(255,255,255,0.85)'}>
                    {lbl.split('/').pop()}
                  </text>
                </g>
                {isBlocked && (
                  <>
                    <line x1={x+4} y1={y+4} x2={x+w-4} y2={y+h-4}
                      stroke="#EF4444" strokeWidth="1.5" opacity="0.5" style={{ pointerEvents:'none' }}/>
                    <line x1={x+w-4} y1={y+4} x2={x+4} y2={y+h-4}
                      stroke="#EF4444" strokeWidth="1.5" opacity="0.5" style={{ pointerEvents:'none' }}/>
                  </>
                )}
                </g>
              </g>
            )
          })}
        </g>
      </svg>

      {tooltip && (
        <div className="absolute z-50 pointer-events-none" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="bg-bg-panel border border-border rounded shadow-xl text-xs min-w-[148px]">
            <div className="px-2.5 py-1.5 border-b border-border font-mono font-bold text-text-primary text-[11px]">
              {tooltip.lbl}
            </div>
            <div className="px-2.5 py-1.5 flex flex-col gap-0.5 text-[10px] text-text-muted">
              <span>Ряд <span className="text-text-secondary">{tooltip.rack.row_index + 1}</span> · Стойка <span className="text-text-secondary">{tooltip.rack.rack_index + 1}</span></span>
              {(() => {
                const tooltipTemp = getRackTemperature(tooltip.rack, telemetryMap)
                const tooltipThermal = getThermalColor(tooltipTemp)
                if (tooltipTemp == null) return null
                return (
                  <div className="flex items-center justify-between gap-2 pt-0.5 mt-0.5 border-t border-border/50 text-[10px]">
                    <span>Температура:</span>
                    <span
                      className="font-mono font-bold px-1 py-0.5 rounded text-[10px]"
                      style={{ color: tooltipThermal?.stroke ?? '#94a3b8', background: tooltipThermal?.badgeBg ?? 'transparent' }}
                    >
                      {tooltipTemp.toFixed(1)}°C
                    </span>
                  </div>
                )
              })()}
              {tooltip.rack.is_blocked && <span className="text-red-400">⊘ Заблокирована</span>}
              {tooltip.rack.id === srcId && <span className="text-yellow-400 font-semibold">▶ Источник (A)</span>}
              {tooltip.rack.id === tgtId && <span className="text-emerald-400 font-semibold">▶ Назначение (B)</span>}
            </div>
          </div>
        </div>
      )}

      <div className="absolute bottom-3 right-3 flex flex-col gap-1.5 z-20">
        {[
          { l: '+', title: 'Увеличить', testId: 'zoom-in',   fn: () => setTr(t => ({ ...t, zoom: Math.min(t.zoom * 1.3, 10) })) },
          { l: '−', title: 'Уменьшить', testId: 'zoom-out',  fn: () => setTr(t => ({ ...t, zoom: Math.max(t.zoom * 0.77, 0.08) })) },
          { l: '⌂', title: 'Сбросить вид', testId: 'zoom-reset', fn: fitView },
        ].map(({ l, title, testId, fn }) => (
          <button
            key={l}
            data-testid={testId}
            onClick={fn}
            title={title}
            aria-label={title}
            className="w-11 h-11 min-w-[44px] min-h-[44px] flex items-center justify-center bg-bg-panel/95 border border-border rounded-md text-text-muted hover:bg-bg-hover hover:text-text-primary active:scale-95 text-base font-semibold shadow-md transition-all cursor-pointer select-none"
          >
            {l}
          </button>
        ))}
      </div>

      <div className="absolute bottom-3 left-3 flex flex-wrap items-center gap-3 text-[10px] text-text-muted pointer-events-none max-w-[calc(100%-120px)]">
        {(srcId != null || tgtId != null) && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border-2 border-yellow-400 bg-yellow-400/20"/>
              A — источник
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border-2 border-emerald-400 bg-emerald-400/20"/>
              B — назначение
            </span>
          </>
        )}
        {telemetryMap && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-xs" style={{ backgroundColor: 'rgba(2, 132, 199, 0.4)', border: '1px solid #0284c7' }}/>
              &lt;25°C
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-xs" style={{ backgroundColor: 'rgba(245, 158, 11, 0.4)', border: '1px solid #f59e0b' }}/>
              25–30°C
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-xs" style={{ backgroundColor: 'rgba(239, 68, 68, 0.5)', border: '1px solid #ef4444' }}/>
              &gt;30°C
            </span>
          </>
        )}
        {aisleRects.length > 0 && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border border-sky-400/60 bg-sky-400/20"/>
              Холодный
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border border-red-400/60 bg-red-400/20"/>
              Горячий
            </span>
            {aisleRects.some(z => z.isContained) && (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm border border-dashed border-sky-400 bg-sky-400/20"/>
                Изоляция
              </span>
            )}
          </>
        )}
      </div>

      <div className="absolute top-2 right-3 text-[9px] text-text-muted/40 font-mono pointer-events-none select-none" title="Масштаб схемы">
        {calculatorMapScaleLabel(tr.zoom)}
      </div>
    </div>
  )
}
